import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import { uniq } from 'lodash';
import { Card, Button } from '@vwfs-bronson/bronson-react';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import { BundledProductWithMarketingInfo, FR_TIMEZONE, Contract, MyProfileData } from 'cpfr-common';
import { MarketingCardButton } from '../MarketingCardButton';
import { currentBrand } from '../../../config';
import { MarketingCard, YEARS_FOR_UNIFIED_MAINTENANCE_SUBSCRIPTION } from '../utils';

type MarketingCardUiProps = {
    productsWithMaintenance?: BundledProductWithMarketingInfo[];
    otherProducts?: BundledProductWithMarketingInfo[];
    content?: MarketingCard;
    onlineServicePlanUrl?: string;
    contracts?: Contract[];
    setShouldRender: (shouldRender: boolean) => void;
    profileData?: MyProfileData;
};

enum Brands {
    vw = 'vw',
    skoda = 'skoda',
    audi = 'audi',
    seat = 'seat',
}

export const MarketingCardUi: React.FC<MarketingCardUiProps> = ({
    productsWithMaintenance = [],
    otherProducts = [],
    content,
    onlineServicePlanUrl,
    contracts,
    setShouldRender,
    profileData,
}) => {
    const linkBrand =
        currentBrand === 'vwfs' && contracts?.filter((c) => c.isActive).length === 1
            ? (contracts.find((c) => c.isActive)?.brand?.toLocaleLowerCase() as Brands)
            : (currentBrand.toLocaleLowerCase() as Brands);

    const isMaintenanceSubscriptionAvailable = (): boolean => {
        const { maintenanceSubscription } = content as MarketingCard;
        const currentDate = moment().tz(FR_TIMEZONE);
        if (!onlineServicePlanUrl) return false;
        if (profileData?.identification?.customerType !== 'Retail') return false;
        if (
            productsWithMaintenance.length > 0 &&
            productsWithMaintenance.some((product) => product?.status === 'ACTIVE')
        )
            return false;
        if (!maintenanceSubscription?.traciLinks[linkBrand]) return false;

        return otherProducts
            .map((product) => {
                if (!product.firstRegistrationDate) return false;
                const maintenanceSubscriptionDeadline = formatCpDate(product.firstRegistrationDate)
                    .toMoment()
                    .add(YEARS_FOR_UNIFIED_MAINTENANCE_SUBSCRIPTION, 'years');

                return currentDate.isBefore(maintenanceSubscriptionDeadline);
            })
            .some(Boolean);
    };

    const isInvoiceCardAvailable = (): boolean => {
        const { invoice } = content as MarketingCard;
        if (!invoice) return false;
        if (profileData?.identification?.customerType === 'Retail') return false;
        return true;
    };

    const isMotorInsuranceAvailable = (): boolean => {
        const { motorInsurance } = content as MarketingCard;
        if (!motorInsurance.links[linkBrand]) return false;
        return !!content?.motorInsurance.imageUrl && profileData?.identification?.customerType === 'Retail';
    };

    useEffect(() => {
        if (content) {
            if (isMaintenanceSubscriptionAvailable() || isMotorInsuranceAvailable() || isInvoiceCardAvailable()) {
                setShouldRender(true);
            }
        }
        // eslint-disable-next-line
    }, [content, contracts, productsWithMaintenance, otherProducts]);

    if (!content || (!productsWithMaintenance.length && !otherProducts.length)) {
        return null;
    }

    const getMotorInsuranceButtons = (): JSX.Element[] => {
        const brand = currentBrand.toLocaleLowerCase();
        let buttons: JSX.Element[] = [];
        if (brand !== 'vwfs')
            buttons = [
                <MarketingCardButton
                    key={`motor-insurance-card-${brand}`}
                    brand={brand}
                    isMultipleMatch={false}
                    content={content}
                    cardType="MOTOR_INSURANCE"
                />,
            ];

        const productsBrands: string[] = uniq(
            productsWithMaintenance.map((product) => product.carCode).filter(Boolean) as string[],
        );
        buttons = productsBrands.map((productBrand) => (
            <MarketingCardButton
                key={`motor-insurance-card-${productBrand}`}
                brand={productBrand}
                isMultipleMatch={productsBrands.length > 1}
                content={content}
                cardType="MOTOR_INSURANCE"
            />
        ));

        if (content?.motorInsurance.additionalButtons) {
            const additionalButtons = content.motorInsurance.additionalButtons.map((button, index) => (
                <MarketingCardButton
                    key={`motor-insurance-card-additional-button-${index}`}
                    brand={linkBrand}
                    isMultipleMatch={false}
                    content={content}
                    cardType="MOTOR_INSURANCE"
                    buttonText={button.text}
                    buttonLink={button.link}
                />
            ));
            buttons = [...buttons, ...additionalButtons];
        }

        return buttons;
    };

    const maintenanceSubscriptionCard = (
        <Card
            testId="maintenance-subscription-card"
            title={content?.maintenanceSubscription.title || ''}
            imageSrc={'/' + content?.maintenanceSubscription.imageUrl || ''}
            buttons={[
                <MarketingCardButton
                    key={`maintenance-subscription-card-button`}
                    onlineServicePlanUrl={onlineServicePlanUrl}
                    content={content}
                    cardType="MAINTENANCE_SUBSCRIPTION"
                />,
            ]}
            footer
        >
            <span className={'u-font-size-fs-1'}>{content?.maintenanceSubscription.text}</span>
        </Card>
    );

    const motorInsuranceCard = (
        <Card
            testId="motor-insurance-card"
            title={content?.motorInsurance.title || ''}
            imageSrc={'/' + content?.motorInsurance.imageUrl || ''}
            buttons={getMotorInsuranceButtons()}
            footer
        >
            <span className={'u-font-size-fs-1'}>{content?.motorInsurance.text}</span>
        </Card>
    );

    const invoiceButtons = [
        <Button
            link={true}
            key={`invoice-card-button-1`}
            icon="semantic-forward"
            iconReversed
            small
            onClick={() => window.open(content?.invoice?.['link-1'], '_blank', 'noopener,noreferrer')}
            element="a"
            aria-label={content?.invoice?.buttonText || ''}
            title={content?.invoice?.buttonText || ''}
        >
            {content?.invoice?.buttonText || ''}
        </Button>,
        <Button
            link={true}
            key={`invoice-card-button-2`}
            icon="semantic-forward"
            iconReversed
            small
            onClick={() => window.open(content?.invoice?.['link-2'], '_blank', 'noopener,noreferrer')}
            element="a"
            aria-label={content?.invoice?.buttonText2 || ''}
            title={content?.invoice?.buttonText2 || ''}
        >
            {content?.invoice?.buttonText2 || ''}
        </Button>,
    ];

    const invoiceCard = (
        <Card
            testId="invoice-card"
            title={content?.invoice?.title || ''}
            imageSrc={'/' + content?.invoice?.imageUrl || ''}
            buttons={invoiceButtons}
            footer
        >
            <div className={'u-font-size-fs-1'}>{content?.invoice?.header}</div>
            <div className={'u-font-size-fs-1'}>{content?.invoice?.description}</div>
            <div className={'u-font-size-fs-1'}>{content?.invoice?.description2}</div>
        </Card>
    );

    return (
        <>
            {isInvoiceCardAvailable() && <div className="u-mb-small">{invoiceCard}</div>}
            {isMaintenanceSubscriptionAvailable() && <div className="u-mb-small">{maintenanceSubscriptionCard}</div>}
            {isMotorInsuranceAvailable() && motorInsuranceCard}
        </>
    );
};
